import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { fetchMenuItems, MenuItem } from '../services/menuService';
import { useMyEpcRoles } from '../features/epc/dashboards/hooks/useMyEpcRoles';
import { ChevronDownIcon } from '../icons';
import { getFeatureIcon } from '../features/feature/utils/featureIcons';

const EPC_DASHBOARD_ITEMS: Array<MenuItem & { allowedEpcRoles: string[] }> = [
  
  {
    id: 'epc-dashboard-department-head',
    name: 'Department Head Dashboard',
    path: '/epc/dashboards/department-head',
    icon: 'UsersIcon',
    permissionKey: 'epcDashboard.read',
    order: 9002,
    allowedEpcRoles: ['department_head'],
  },
  {
    id: 'epc-dashboard-department-manager',
    name: 'Department Manager Dashboard',
    path: '/epc/dashboards/department-manager',
    icon: 'UserGroupIcon',
    permissionKey: 'epcDashboard.read',
    order: 9003,
    allowedEpcRoles: ['manager'],
  },
];
import SoriginLogoBanner from './branding/SoriginLogoBanner';
import { SoriginWordmark, SoriginMonogram } from './branding/SoriginWordmark';
import SoriginSidebarBackdrop from './branding/SoriginSidebarBackdrop';
import WhatsNewLink from '../features/whats-new/WhatsNewLink';

/**
 * Sidebar icon renderer. Reuses the semantic resolver from
 * `featureIcons.tsx` so menus and sub-menus in the sidebar get the same
 * modern Lucide icons as the /admin/features view. Falls back to the
 * uploaded image (data: / http(s) / leading-slash URLs) when the admin
 * uploaded a custom image via the icon picker.
 */
function MenuIcon({ item, className = 'w-5 h-5' }: { item: MenuItem; className?: string }) {
  const raw = item.icon ?? '';
  const isImage =
    raw.startsWith('data:image/') ||
    raw.startsWith('http://') ||
    raw.startsWith('https://') ||
    raw.startsWith('/');
  if (isImage) {
    return (
      <img
        src={raw}
        alt=""
        className={className}
        style={{ objectFit: 'contain' }}
      />
    );
  }
  const Icon = getFeatureIcon({
    icon: raw,
    permissionKey: item.permissionKey ?? '',
    key: item.id,
    route: item.path ?? '',
  } as any);
  return <Icon className={className} aria-hidden />;
}

/**
 * Dynamic Sidebar Component
 * 
 * A professional, responsive sidebar that dynamically loads menu items based on user permissions.
 * Features include:
 * - Dynamic menu loading from backend API
 * - Smooth animations and transitions
 * - Responsive design with mobile support
 * - Auto-expand submenus based on current route
 * - Professional color scheme with proper contrast
 * - Accessibility features
 * - Hover effects and active states
 * 
 * @component
 * @returns {JSX.Element | null} The sidebar component or null if user not authenticated
 */
const DynamicSidebar: React.FC = () => {
  // ============================================================================
  // CONTEXT & ROUTER HOOKS
  // ============================================================================
  const { user, activeDepartment } = useAuth();
  const { isExpanded, isMobileOpen, isHovered, openSubmenu, toggleSubmenu } = useSidebar();
  const location = useLocation();
  const isEpcContext = (activeDepartment ?? '').toUpperCase() === 'EPC';
  const myEpcRolesQuery = useMyEpcRoles({ enabled: isEpcContext });
  // Stable string fingerprint of the user's EPC roles. Used to derive the
  // sidebar's dashboard entries without putting an array reference into
  // any callback's dependency list (which would loop).
  const myEpcRolesKey = useMemo(
    () => (myEpcRolesQuery.data ?? []).slice().sort().join(','),
    [myEpcRolesQuery.data]
  );

  // ============================================================================
  // LOCAL STATE MANAGEMENT
  // ============================================================================
  const [backendMenuItems, setBackendMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submenuHeights, setSubmenuHeights] = useState<Record<string, number>>({});
  // Flyout panel — tracks which item is hovered (null = panel hidden)
  const [hoveredItem, setHoveredItem] = useState<{ item: MenuItem; rect: DOMRect } | null>(null);

  // ============================================================================
  // REFS FOR DOM MANIPULATION
  // ============================================================================
  const submenuRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Timer ref for delayed panel close (allows mouse to travel from icon → panel)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelCloseTimer = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  const startCloseTimer = useCallback(() => {
    cancelCloseTimer();
    closeTimerRef.current = setTimeout(() => setHoveredItem(null), 120);
  }, [cancelCloseTimer]);

  const clearHoveredItem = useCallback(() => setHoveredItem(null), []);

  // ============================================================================
  // COLOR SCHEME CONSTANTS — "Sovereign Emerald" design register
  // ----------------------------------------------------------------------------
  // Cream banknote paper + engraved racing-green ink + champagne-gold accent.
  // Light, flat, printed-looking — never dark, never glowing. The active state
  // uses a champagne wash + champagne left rail + champagne-tinted icon tile;
  // idle is espresso ink on transparent paper; hover is a faint racing-green
  // wash. Defined inline (not imported) to keep this shared component
  // self-contained.
  // ============================================================================
  const SE = {
    paper: '#F2EADB',                    // sidebar surface
    header: '#F8F2E2',                   // logo header / candlelight
    raised: 'rgba(250,247,238,0.96)',    // raised cards (footer)
    hairline: 'rgba(11,59,46,0.14)',     // outer border (soft — shadow carries the visual weight)
    hairlineSoft: 'rgba(11,59,46,0.16)', // inner dividers
    ink: '#2A1F17',                      // espresso — idle labels
    title: '#0B3B2E',                    // racing green — active labels / titles
    bottle: '#1F5A47',                   // deep green — active icon
    brand: '#339274',                    // brand green — online dot, progress
    champagne: '#C9A961',                // metallic accent — active rail
    champagneWash: 'rgba(201,169,97,0.14)',
    champagneTile: 'rgba(201,169,97,0.20)',
    champagneRing: 'rgba(201,169,97,0.50)',
    hoverWash: 'rgba(11,59,46,0.05)',
    muted: '#7C8A82',                    // captions, idle icons, section headers
  };

  // ============================================================================
  // MENU LOADING LOGIC
  // ============================================================================
  
  /**
   * Fetches menu items from the backend API
   * Handles loading states and error cases gracefully
   * Note: Backend already filters menu items based on user permissions
   */
  const loadMenuItems = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading menu items for user:', user);
      const items = await fetchMenuItems(activeDepartment || 'OMS');
      const menuArray = Array.isArray(items) ? items : [];
      setBackendMenuItems(menuArray);
      console.log('📋 Menu items loaded successfully (already filtered by backend):', menuArray);
      console.log('📋 Number of menu items:', menuArray.length);
    } catch (error) {
      console.error('❌ Failed to load menu items:', error);
      setBackendMenuItems([]);
    } finally {
      setLoading(false);
    }
  }, [user, activeDepartment]);

  // Load menu items when user is authenticated
  useEffect(() => {
    if (user) {
      loadMenuItems();
    }
  }, [user, loadMenuItems]);

  // Derive the final menu (backend items + role-gated dashboard injections).
  // useMemo with a string key avoids array-identity churn on every render.
  //
  // Note on dedupe: the backend feature catalog already seeds the EPC dashboard
  // entries (epc-dashboard-{project,department}-{head,manager}) into the menu
  // service. We also inject the same entries from EPC_DASHBOARD_ITEMS here so
  // that role-gating works for users whose menu permissions aren't in sync.
  // Merging both lists naively produces duplicate React keys, so we dedupe by
  // `id` — the static role-gated entry wins (it's appended last and overwrites
  // the backend one), which gives us consistent ordering + icons.
  const menuItems = useMemo<MenuItem[]>(() => {
    const isEpc = (activeDepartment ?? 'OMS').toUpperCase() === 'EPC';
    if (!isEpc || !myEpcRolesKey) return backendMenuItems;
    const roleSet = new Set(myEpcRolesKey.split(',').filter(Boolean));
    const dashboardEntries: MenuItem[] = EPC_DASHBOARD_ITEMS
      .filter((d) => d.allowedEpcRoles.some((r) => roleSet.has(r)))
      .map(({ allowedEpcRoles: _r, ...rest }) => rest);
    if (dashboardEntries.length === 0) return backendMenuItems;

    // When the backend returned items (i.e. the role has a saved menu config or
    // permission-based items), only inject an EPC dashboard entry if the backend
    // already included it — this prevents frontend role-based injection from
    // bypassing the role's saved menu order.  When the backend returned nothing
    // (no config, no permissions), fall back to the full role-gated set.
    const backendIds = new Set(backendMenuItems.map((it) => it.id));
    const entriesToMerge = backendMenuItems.length > 0
      ? dashboardEntries.filter((d) => backendIds.has(d.id))
      : dashboardEntries;

    if (entriesToMerge.length === 0) return backendMenuItems;

    // Dedupe by id; backend version wins (comes first in the spread).
    const seen = new Set<string>();
    const merged: MenuItem[] = [];
    for (const it of [...backendMenuItems, ...entriesToMerge]) {
      if (seen.has(it.id)) continue;
      seen.add(it.id);
      merged.push(it);
    }
    return merged;
  }, [backendMenuItems, activeDepartment, myEpcRolesKey]);

  // ============================================================================
  // AUTO-EXPAND SUBMENU LOGIC
  // ============================================================================
  
  /**
   * Automatically expands submenus based on the current route
   * Ensures users can see the active menu item in context
   */
  useEffect(() => {
    const checkAndOpenSubmenu = (items: MenuItem[]) => {
      // Ensure items is an array before calling forEach
      if (!Array.isArray(items)) {
        console.warn('checkAndOpenSubmenu received non-array items:', items);
        return;
      }
      
      items.forEach(item => {
        if (item.subItems && item.subItems.length > 0) {
          const hasActiveChild = item.subItems.some(subItem => 
            location.pathname === subItem.path
          );
          if (hasActiveChild && openSubmenu !== item.id) {
            toggleSubmenu(item.id);
          }
        }
      });
    };

    checkAndOpenSubmenu(menuItems);
  }, [location.pathname, menuItems]);

  // ============================================================================
  // SUBMENU ANIMATION LOGIC
  // ============================================================================
  
  /**
   * Calculates submenu heights for smooth height animations
   * Uses refs to measure actual DOM element heights
   */
  useEffect(() => {
    const calculateHeights = () => {
      const heights: Record<string, number> = {};
      
      Object.keys(submenuRefs.current).forEach(key => {
        const ref = submenuRefs.current[key];
        if (ref) {
          heights[key] = ref.scrollHeight;
        }
      });
      
      setSubmenuHeights(heights);
    };

    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(calculateHeights, 100);
    return () => clearTimeout(timer);
  }, [openSubmenu]);

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================
  
  /**
   * Checks if a given path matches the current location
   * @param path - The path to check
   * @returns boolean indicating if the path is active
   */
  const isActive = useCallback((path: string) => {
    return location.pathname === path;
  }, [location.pathname]);

  /**
   * Checks if a submenu is currently open
   * @param itemId - The menu item ID to check
   * @returns boolean indicating if the submenu is open
   */
  const isSubmenuOpen = (itemId: string) => openSubmenu === itemId;

  /**
   * Handles submenu toggle with proper state management
   * @param itemId - The menu item ID to toggle
   */
  const handleSubmenuToggle = (itemId: string) => {
    toggleSubmenu(itemId);
  };

  // ============================================================================
  // RENDER FUNCTIONS
  // ============================================================================
  
  /**
   * Renders an individual menu item with proper styling and interactions
   * Handles both regular menu items and submenu parent items
   * 
   * @param item - The menu item to render
   * @returns JSX element for the menu item
   */
  const renderMenuItem = (item: MenuItem) => {
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isItemActive = item.path ? isActive(item.path) : false;
    const isSubmenuItemOpen = isSubmenuOpen(item.id);
    const submenuHeight = submenuHeights[item.id] || 0;

    const hasActiveChild = !!item.subItems?.some(s => isActive(s.path));
    const parentActive = isItemActive || hasActiveChild;
    const collapsed = !isExpanded && !isHovered && !isMobileOpen;

    // Shared row classes for visual parity between <button> and <Link>.
    // Color-bearing properties live in inline `style` (SE tokens); only
    // layout/spacing/rounded/transition stays in className.
    const rowBase =
      'group relative flex items-center w-full gap-3 rounded-xl text-[13px] tracking-tight transition-all duration-200';
    const rowSize = collapsed ? 'justify-center px-0 py-2.5' : 'justify-start pl-3 pr-2.5 py-2.5';

    // Row container style: active = champagne wash; idle = transparent.
    const rowStyle = (active: boolean): React.CSSProperties => ({
      backgroundColor: active ? SE.champagneWash : 'transparent',
      color: active ? SE.title : SE.ink,
      fontWeight: active ? 700 : 500,
    });

    // Icon-tile style for parent/leaf rows.
    const tileStyle = (active: boolean): React.CSSProperties =>
      active
        ? {
            backgroundColor: SE.champagneTile,
            color: SE.bottle,
            boxShadow: `inset 0 0 0 1px ${SE.champagneRing}`,
          }
        : { backgroundColor: 'transparent', color: SE.muted };

    return (
      <li key={item.id} className="relative">
        {hasSubItems ? (
          // ── PARENT WITH SUBMENU ────────────────────────────────────────────
          <button
            onClick={() => handleSubmenuToggle(item.id)}
            className={`${rowBase} ${rowSize}`}
            style={rowStyle(parentActive)}
            onMouseEnter={(e) => {
              if (collapsed) {
                cancelCloseTimer();
                setHoveredItem({ item, rect: e.currentTarget.getBoundingClientRect() });
              } else if (!parentActive) {
                e.currentTarget.style.backgroundColor = SE.hoverWash;
                e.currentTarget.style.color = SE.title;
              }
            }}
            onMouseLeave={(e) => {
              if (collapsed) {
                startCloseTimer();
              } else if (!parentActive) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = SE.ink;
              }
            }}
            aria-expanded={isSubmenuItemOpen}
            aria-haspopup="true"
          >
            {/* Left champagne accent rail (3px when active) */}
            <span
              aria-hidden
              className={`absolute left-0 top-1.5 bottom-1.5 rounded-r-full transition-all duration-200 ${
                parentActive ? 'opacity-100 scale-y-100 w-[3px]' : 'opacity-0 scale-y-50 w-[3px]'
              }`}
              style={{ backgroundColor: SE.champagne }}
            />

            <span
              className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 group-hover:[box-shadow:inset_0_0_0_1px_rgba(11,59,46,0.16)]"
              style={tileStyle(parentActive)}
            >
              <MenuIcon item={item} className="w-[18px] h-[18px]" />
            </span>

            {!collapsed && (
              <>
                <span className="flex-1 text-left truncate tracking-[0.005em]">{item.name}</span>
                <ChevronDownIcon
                  className="w-3.5 h-3.5 transition-transform duration-300 ease-out"
                  style={{
                    transform: isSubmenuItemOpen ? 'rotate(180deg)' : undefined,
                    color: isSubmenuItemOpen ? SE.title : SE.muted,
                  }}
                />
              </>
            )}
          </button>
        ) : (
          // ── LEAF MENU ITEM ─────────────────────────────────────────────────
          <Link
            to={item.path || '#'}
            className={`${rowBase} ${rowSize}`}
            style={rowStyle(isItemActive)}
            onMouseEnter={(e) => {
              if (collapsed) {
                cancelCloseTimer();
                setHoveredItem({ item, rect: e.currentTarget.getBoundingClientRect() });
              } else if (!isItemActive) {
                e.currentTarget.style.backgroundColor = SE.hoverWash;
                e.currentTarget.style.color = SE.title;
              }
            }}
            onMouseLeave={(e) => {
              if (collapsed) {
                startCloseTimer();
              } else if (!isItemActive) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = SE.ink;
              }
            }}
          >
            <span
              aria-hidden
              className={`absolute left-0 top-1.5 bottom-1.5 rounded-r-full transition-all duration-200 ${
                isItemActive ? 'opacity-100 scale-y-100 w-[3px]' : 'opacity-0 scale-y-50 w-[3px]'
              }`}
              style={{ backgroundColor: SE.champagne }}
            />

            <span
              className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 group-hover:[box-shadow:inset_0_0_0_1px_rgba(11,59,46,0.16)]"
              style={tileStyle(isItemActive)}
            >
              <MenuIcon item={item} className="w-[18px] h-[18px]" />
            </span>

            {!collapsed && (
              <span className="flex-1 text-left truncate tracking-[0.005em]">{item.name}</span>
            )}
          </Link>
        )}

        {/* ── SUBMENU PANEL ───────────────────────────────────────────────── */}
        {hasSubItems && !collapsed && (
          <div
            ref={el => {
              submenuRefs.current[item.id] = el;
            }}
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{
              height: isSubmenuItemOpen ? `${submenuHeight}px` : '0px',
              opacity: isSubmenuItemOpen ? 1 : 0,
            }}
          >
            <ul className="relative mt-1 ml-[1.85rem] pl-3.5 space-y-0.5">
              {/* Vertical rail (hairline) */}
              <span
                aria-hidden
                className="absolute left-0 top-0 bottom-1 w-px"
                style={{ backgroundColor: SE.hairlineSoft }}
              />
              {item.subItems!.map(subItem => {
                const subActive = isActive(subItem.path);
                return (
                  <li key={subItem.id} className="group/sub relative">
                    {/* Horizontal connector rail that meets the vertical rail when active */}
                    <span
                      aria-hidden
                      className={`absolute -left-3.5 top-1/2 -translate-y-1/2 h-px transition-all duration-200 ${
                        subActive
                          ? 'w-3'
                          : 'w-2 opacity-0 group-hover/sub:opacity-100'
                      }`}
                      style={{
                        backgroundColor: subActive ? SE.champagne : 'rgba(11,59,46,0.18)',
                      }}
                    />
                    <Link
                      to={subItem.path}
                      className="relative flex items-center gap-2.5 pl-2 pr-2 py-1.5 rounded-lg text-[12.5px] tracking-tight transition-all duration-200"
                      style={{
                        backgroundColor: subActive ? SE.champagneWash : 'transparent',
                        color: subActive ? SE.title : SE.ink,
                        fontWeight: subActive ? 600 : 500,
                      }}
                      onMouseEnter={(e) => {
                        if (!subActive) {
                          e.currentTarget.style.backgroundColor = SE.hoverWash;
                          e.currentTarget.style.color = SE.title;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!subActive) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = SE.ink;
                        }
                      }}
                    >
                      <span
                        className="inline-flex items-center justify-center w-6 h-6 rounded-md flex-shrink-0 transition-all duration-200"
                        style={
                          subActive
                            ? {
                                backgroundColor: SE.champagneTile,
                                color: SE.bottle,
                                boxShadow: `inset 0 0 0 1px ${SE.champagneRing}`,
                              }
                            : { backgroundColor: 'transparent', color: SE.muted }
                        }
                      >
                        <MenuIcon item={subItem} className="w-[14px] h-[14px]" />
                      </span>
                      <span className="flex-1 truncate">{subItem.name}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </li>
    );
  };

  // ============================================================================
  // EARLY RETURNS
  // ============================================================================
  
  // Don't render if user is not authenticated
  if (!user) return null;

  // ============================================================================
  // LOADING STATE
  // ============================================================================
  if (loading) {
    return (
      <aside
        className="fixed mt-16 flex flex-col lg:mt-0 top-0 left-0 h-screen w-[272px]"
        style={{
          backgroundColor: SE.paper,
          color: SE.ink,
          borderRight: `1px solid ${SE.hairline}`,
          boxShadow: '1px 0 0 rgba(201,169,97,0.08), 4px 0 16px rgba(11,59,46,0.07), 10px 0 36px rgba(11,59,46,0.04)',
        }}
      >
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div
              className="animate-spin rounded-full h-7 w-7 border-2 mx-auto mb-3"
              style={{ borderColor: SE.hairlineSoft, borderTopColor: SE.brand }}
            ></div>
            <p className="text-xs font-medium" style={{ color: SE.muted }}>Loading menu…</p>
          </div>
        </div>
      </aside>
    );
  }

  // ============================================================================
  // MAIN COMPONENT RENDER
  // ============================================================================
  return (
    <>
      {/* Flyout panel animation keyframes */}
      <style>{`
        @keyframes flyout-in {
          from { opacity: 0; transform: translateX(-8px) scale(0.97); }
          to   { opacity: 1; transform: translateX(0)   scale(1);    }
        }
      `}</style>

      <aside
        className={`
          fixed mt-16 flex flex-col lg:mt-0 top-0 left-0 h-screen transition-[width] duration-300 ease-out z-50
          ${isExpanded || isMobileOpen ? 'w-[272px]' : 'w-[76px]'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
        `}
        style={{
          backgroundColor: SE.paper,
          color: SE.ink,
          borderRight: `1px solid ${SE.hairline}`,
          boxShadow: '1px 0 0 rgba(201,169,97,0.08), 4px 0 16px rgba(11,59,46,0.07), 10px 0 36px rgba(11,59,46,0.04)',
        }}
      >
        {/* ── BANKNOTE BACKDROP (fills entire sidebar, sits behind all content) ─ */}
        <SoriginSidebarBackdrop className="absolute inset-0" />

        {/* ── LOGO ───────────────────────────────────────────────────────── */}
        <div
          className="relative z-10 h-[76px] px-3 flex items-center justify-center border-b overflow-hidden"
          style={{ backgroundColor: SE.paper, borderColor: SE.hairlineSoft }}
        >
          {(isExpanded || isHovered || isMobileOpen) && (
            <SoriginLogoBanner className="absolute inset-0" />
          )}
          <Link to="/" className="relative z-10 flex items-center justify-center w-full" aria-label="Home">
            {isExpanded || isHovered || isMobileOpen ? (
              <SoriginWordmark className="h-7 w-auto" />
            ) : (
              /* Collapsed: isolated animated wind turbine, reuses sb-turbine-blades animation from SoriginLogoBanner.css */
              <svg
                viewBox="30 34 40 75"
                width="61"
                height="56"
                aria-hidden="true"
                style={{ display: 'block', overflow: 'visible' }}
              >
                <defs>
                  <linearGradient id="sc-pole" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#006346" stopOpacity="0.72" />
                    <stop offset="100%" stopColor="#076333" stopOpacity="0.55" />
                  </linearGradient>
                  <radialGradient id="sc-halo" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#006346" stopOpacity="0.07" />
                    <stop offset="100%" stopColor="#006346" stopOpacity="0" />
                  </radialGradient>
                </defs>

                {/* Subtle halo glow behind turbine */}
                <ellipse cx="60" cy="68" rx="28" ry="32" fill="url(#sc-halo)" />

                {/* Rosette rings (faint engraved ornament) */}
                <g transform="translate(60,62)" fill="none" stroke="#006346" strokeWidth="0.45">
                  <circle r="26" strokeOpacity="0.09" />
                  <circle r="20" strokeOpacity="0.13" strokeDasharray="0.6 2.2" />
                  <circle r="13" strokeOpacity="0.08" />
                </g>

                {/* Ground hatching */}
                <g stroke="#006346" strokeOpacity="0.30" strokeWidth="0.5" strokeLinecap="round">
                  <line x1="48" y1="106" x2="72" y2="106" />
                  <line x1="44" y1="109" x2="76" y2="109" strokeDasharray="1 2" strokeOpacity="0.20" />
                </g>

                {/* Contact shadow */}
                <ellipse cx="60" cy="103.5" rx="10" ry="1.2" fill="#006346" fillOpacity="0.18" />

                {/* Pole */}
                <path d="M 58.5 103 L 59.6 51 L 60.4 51 L 61.5 103 Z" fill="url(#sc-pole)" />

                {/* Nacelle */}
                <path d="M 56.5 53 L 60 47.5 L 63.5 53 Z" fill="#006346" fillOpacity="0.58" />
                <rect x="57.8" y="49" width="4.4" height="3.2" rx="0.6" fill="#006346" fillOpacity="0.52" />

                {/* Blades — reuses the sb-turbine-blades CSS animation */}
                <g transform="translate(60,50)">
                  <g className="sb-turbine-blades">
                    <path d="M 2.6 0 Q 12 -1.5 22 -0.6 Q 28 0 22 0.6 Q 12 1.5 2.6 0 Z" fill="#006346" fillOpacity="0.68" />
                    <path d="M 2.6 0 Q 12 -1.5 22 -0.6 Q 28 0 22 0.6 Q 12 1.5 2.6 0 Z" fill="#006346" fillOpacity="0.68" transform="rotate(120)" />
                    <path d="M 2.6 0 Q 12 -1.5 22 -0.6 Q 28 0 22 0.6 Q 12 1.5 2.6 0 Z" fill="#006346" fillOpacity="0.68" transform="rotate(240)" />
                  </g>
                  {/* Hub cap */}
                  <circle r="2.8" fill="#C9A961" />
                  <circle r="2.8" fill="none" stroke="#006346" strokeOpacity="0.80" strokeWidth="0.5" />
                  <circle r="1" fill="#006346" />
                </g>

                {/* Champagne sparkles */}
                <circle cx="42" cy="44" r="0.8" fill="#C9A961" fillOpacity="0.65" />
                <circle cx="79" cy="58" r="0.6" fill="#C9A961" fillOpacity="0.55" />
                <circle cx="51" cy="96" r="0.7" fill="#C9A961" fillOpacity="0.50" />
              </svg>
            )}
          </Link>
        </div>

        {/* ── NAVIGATION ─────────────────────────────────────────────────── */}
        <div className="relative z-10 flex flex-col flex-1 overflow-y-auto no-scrollbar">
          <nav className="flex flex-col p-2.5 pt-3">
            {menuItems.length > 0 ? (
              <div>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <div className="px-3 mb-2 flex items-center gap-2">
                    {/* Champagne tick — tiny engraved accent before the label */}
                    <span
                      aria-hidden
                      className="inline-block w-1.5 h-0.5 rounded-full"
                      style={{ backgroundColor: SE.champagne }}
                    />
                    <span
                      className="text-[10px] uppercase font-bold tracking-[0.16em]"
                      style={{ color: SE.muted }}
                    >
                      Main menu
                    </span>
                    <span className="flex-1 h-px" style={{ backgroundColor: SE.hairlineSoft }} />
                  </div>
                )}
                <ul className="flex flex-col gap-0.5">
                  {menuItems.map(item => renderMenuItem(item))}
                </ul>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-xs" style={{ color: SE.muted }}>No menu items available</p>
              </div>
            )}
          </nav>

          {/* ── WHAT'S NEW ───────────────────────────────────────────────── */}
          {/* Pinned to the bottom group (mt-auto), directly above the user card. */}
          <div
            className="mt-auto px-2.5 pt-2 pb-1"
            style={{ backgroundColor: SE.paper }}
          >
            <WhatsNewLink
              collapsed={!isExpanded && !isHovered && !isMobileOpen}
              activeDepartment={activeDepartment}
            />
          </div>

          {/* ── USER CARD ────────────────────────────────────────────────── */}
          {(isExpanded || isHovered || isMobileOpen) ? (
            <div
              className="p-3 pt-2 border-t"
              style={{ borderTop: `1px dashed ${SE.hairlineSoft}`, backgroundColor: SE.paper }}
            >
              <div
                className="flex items-center gap-3 p-2.5 rounded-xl border transition-all duration-200"
                style={{ backgroundColor: SE.raised, borderColor: 'rgba(11,59,46,0.22)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = SE.champagneRing;
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(42,31,23,0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(11,59,46,0.22)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div className="relative flex-shrink-0">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold tracking-tight"
                    style={{
                      backgroundImage: 'linear-gradient(135deg, #1F5A47, #0B3B2E)',
                      boxShadow: `inset 0 0 0 1px ${SE.champagneRing}`,
                    }}
                  >
                    {user.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <span
                    className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2"
                    style={{ backgroundColor: SE.brand, ['--tw-ring-color' as any]: '#FFFDF8', boxShadow: '0 0 0 2px #FFFDF8' }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-[13px] font-semibold truncate leading-tight tracking-tight"
                    style={{ color: SE.title }}
                  >
                    {user.name || 'User'}
                  </p>
                  <p
                    className="text-[11px] truncate mt-0.5 tracking-tight"
                    style={{ color: SE.muted }}
                  >
                    {typeof user.role === 'object' ? user.role?.name : user.role || 'User'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="p-3 flex justify-center"
              style={{ borderTop: `1px dashed ${SE.hairlineSoft}`, backgroundColor: SE.paper }}
            >
              <div
                title={user.name || 'User'}
                className="relative w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold tracking-tight"
                style={{
                  backgroundImage: 'linear-gradient(135deg, #1F5A47, #0B3B2E)',
                  boxShadow: `inset 0 0 0 1px ${SE.champagneRing}`,
                }}
              >
                {user.name?.charAt(0).toUpperCase() || 'U'}
                <span
                  className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: SE.brand, boxShadow: '0 0 0 2px #FFFDF8' }}
                />
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* ── FLYOUT PANEL ─────────────────────────────────────────────────────────
          Appears on icon hover when the sidebar is collapsed.
          Fixed-positioned next to the hovered item; 8px transparent left padding
          acts as a hover bridge so the mouse can travel from icon → panel without
          the panel disappearing.
      ─────────────────────────────────────────────────────────────────────────── */}
      {hoveredItem && !isExpanded && !isMobileOpen && (() => {
        const { item: fi, rect } = hoveredItem;
        const hasSubItems = fi.subItems && fi.subItems.length > 0;
        // Keep the panel within the viewport vertically
        const panelTop = Math.min(rect.top, Math.max(8, window.innerHeight - (hasSubItems ? fi.subItems!.length * 40 + 60 : 52) - 8));
        const isLeafActive = fi.path ? isActive(fi.path) : false;

        return (
          <div
            className="fixed"
            style={{ left: 76, top: panelTop, paddingLeft: 8, zIndex: 9999, pointerEvents: 'auto' }}
            onMouseEnter={cancelCloseTimer}
            onMouseLeave={startCloseTimer}
          >
            <div
              style={{
                backgroundColor: SE.paper,
                border: `1px solid rgba(11,59,46,0.18)`,
                boxShadow: '0 8px 28px -6px rgba(11,59,46,0.18), 0 2px 10px -2px rgba(11,59,46,0.10)',
                borderRadius: 14,
                minWidth: 208,
                maxWidth: 252,
                overflow: 'hidden',
                animation: 'flyout-in 140ms cubic-bezier(0.16,1,0.3,1) both',
              }}
            >
              {/* Panel header — icon + item name */}
              {hasSubItems ? (
                <div
                  className="flex items-center gap-2.5 px-3 py-2.5"
                  style={{ borderBottom: `1px solid rgba(11,59,46,0.09)` }}
                >
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0"
                    style={{
                      backgroundColor: SE.champagneTile,
                      color: SE.bottle,
                      boxShadow: `inset 0 0 0 1px ${SE.champagneRing}`,
                    }}
                  >
                    <MenuIcon item={fi} className="w-[14px] h-[14px]" />
                  </span>
                  <span className="text-[12.5px] font-bold tracking-tight truncate" style={{ color: SE.title }}>
                    {fi.name}
                  </span>
                </div>
              ) : (
                <Link
                  to={fi.path || '#'}
                  onClick={clearHoveredItem}
                  className="flex items-center gap-2.5 px-3 py-2.5 transition-all duration-150"
                  style={{
                    backgroundColor: isLeafActive ? SE.champagneWash : 'transparent',
                    color: isLeafActive ? SE.title : SE.ink,
                  }}
                  onMouseEnter={(e) => {
                    if (!isLeafActive) e.currentTarget.style.backgroundColor = SE.hoverWash;
                  }}
                  onMouseLeave={(e) => {
                    if (!isLeafActive) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0"
                    style={
                      isLeafActive
                        ? { backgroundColor: SE.champagneTile, color: SE.bottle, boxShadow: `inset 0 0 0 1px ${SE.champagneRing}` }
                        : { backgroundColor: 'rgba(11,59,46,0.06)', color: SE.bottle }
                    }
                  >
                    <MenuIcon item={fi} className="w-[14px] h-[14px]" />
                  </span>
                  <span className="flex-1 text-[13px] font-semibold tracking-tight truncate" style={{ color: SE.title }}>
                    {fi.name}
                  </span>
                  {isLeafActive && (
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: SE.champagne }} />
                  )}
                </Link>
              )}

              {/* Sub-items */}
              {hasSubItems && (
                <ul className="px-2 py-1.5 space-y-px">
                  {fi.subItems!.map(subItem => {
                    const subActive = isActive(subItem.path);
                    return (
                      <li key={subItem.id}>
                        <Link
                          to={subItem.path}
                          onClick={clearHoveredItem}
                          className="group/fp flex items-center gap-2.5 px-2.5 py-[7px] rounded-xl text-[12.5px] tracking-tight transition-all duration-150"
                          style={{
                            backgroundColor: subActive ? SE.champagneWash : 'transparent',
                            color: subActive ? SE.title : SE.ink,
                            fontWeight: subActive ? 600 : 500,
                          }}
                          onMouseEnter={(e) => {
                            if (!subActive) {
                              e.currentTarget.style.backgroundColor = SE.hoverWash;
                              e.currentTarget.style.color = SE.title;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!subActive) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = SE.ink;
                            }
                          }}
                        >
                          <span
                            className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-md flex-shrink-0 transition-all duration-150"
                            style={
                              subActive
                                ? { backgroundColor: SE.champagneTile, color: SE.bottle, boxShadow: `inset 0 0 0 1px ${SE.champagneRing}` }
                                : { backgroundColor: 'transparent', color: SE.muted }
                            }
                          >
                            <MenuIcon item={subItem} className="w-[11px] h-[11px]" />
                          </span>
                          <span className="flex-1 truncate">{subItem.name}</span>
                          {subActive && (
                            <span
                              className="w-1 h-1 rounded-full flex-shrink-0"
                              style={{ backgroundColor: SE.champagne }}
                            />
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default DynamicSidebar;